import { useMemo } from 'react';
import { DndContext } from '@dnd-kit/core';
import type { ComponentRegistry } from './registry';
import { ComponentRegistryContext } from './registry-context';
import { ReportDataContext, type ReportDataset } from './ReportDataContext';
import {
  reportCanvasCollisionDetection,
  reportCanvasMeasuring,
  useReportCanvasDnd,
  type LayoutUpdate,
} from './useReportCanvasDnd';

interface ReportCanvasProviderProps {
  registry: ComponentRegistry;
  datasets: ReportDataset[];
  defaultDatasetKey: string;
  emptyHint?: string;
  onLayoutChange: LayoutUpdate;
  /** Receives DnD state the canvas needs (isAnyDragging). */
  children: (dnd: { isAnyDragging: boolean }) => React.ReactNode;
}

/**
 * Self-contained wrapper for embedded canvases (e.g. a page section): composes
 * the registry context, data context, DndContext, and drag overlay. The
 * palette and canvas must both render inside `children`. Full-page hosts that
 * already own a DndContext (the query builder) use useReportCanvasDnd
 * directly instead.
 */
export function ReportCanvasProvider({
  registry,
  datasets,
  defaultDatasetKey,
  emptyHint,
  onLayoutChange,
  children,
}: ReportCanvasProviderProps) {
  const dnd = useReportCanvasDnd({ registry, onLayoutChange });

  const dataValue = useMemo(
    () => ({ datasets, defaultDatasetKey, emptyHint }),
    [datasets, defaultDatasetKey, emptyHint],
  );

  return (
    <ComponentRegistryContext.Provider value={registry}>
      <ReportDataContext.Provider value={dataValue}>
        <DndContext
          sensors={dnd.sensors}
          collisionDetection={reportCanvasCollisionDetection}
          measuring={reportCanvasMeasuring}
          onDragStart={dnd.onDragStart}
          onDragEnd={dnd.onDragEnd}
          onDragCancel={dnd.onDragCancel}
        >
          {children({ isAnyDragging: dnd.isAnyDragging })}
          {dnd.overlay}
        </DndContext>
      </ReportDataContext.Provider>
    </ComponentRegistryContext.Provider>
  );
}
